/**
 * Client helpers for the admin availability endpoints used by the
 * My availability and Roster surfaces (ADR-0014 V2 Phase 4).
 *
 *   GET    /api/admin/:slug/availability
 *   POST   /api/admin/:slug/availability/rules/replace
 *   POST   /api/admin/:slug/availability/override
 *   POST   /api/admin/:slug/availability/override/:id/decide
 *   DELETE /api/admin/:slug/availability/override/:id
 *   PATCH  /api/admin/:slug/memberships/:userId/confirmed-through
 */

export type StaffType = 'full_time' | 'part_time' | 'paid_per_service' | 'paid_hourly';
export type OverrideStatus = 'pending' | 'approved' | 'rejected';
export type OverrideType = 'time_off' | 'sick' | 'extra';

export interface AvailabilityStaff {
  id: string;
  name: string;
  role: 'owner' | 'manager' | 'staff';
  staffType: StaffType;
  /** Epoch ms at tenant-local midnight, or null. Soft signal — see ADR-0014. */
  availabilityConfirmedThrough: number | null;
}

export interface AvailabilityRule {
  id: string;
  staffId: string;
  /** Bitmask of ISO weekdays, Mon = 1<<0 … Sun = 1<<6. */
  daysOfWeekMask: number;
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  timezone: string;
}

export interface AvailabilityOverride {
  id: string;
  staffId: string;
  rangeStart: number;
  rangeEnd: number;
  type: OverrideType;
  note: string | null;
  status: OverrideStatus;
  decidedAt: number | null;
  decidedBy: string | null;
}

export interface AvailabilityResponse {
  staff: AvailabilityStaff[];
  rules: AvailabilityRule[];
  overrides: AvailabilityOverride[];
}

export class AvailabilityApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: { count?: number },
  ) {
    super(`availability api ${status}: ${code}`);
    this.name = 'AvailabilityApiError';
  }
}

async function send<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as {
      error?: string;
      count?: number;
    };
    throw new AvailabilityApiError(r.status, body.error ?? `http_${r.status}`, {
      count: body.count,
    });
  }
  return r.json() as Promise<T>;
}

export async function fetchAvailability(slug: string): Promise<AvailabilityResponse> {
  return send(await fetch(`/api/admin/${encodeURIComponent(slug)}/availability`));
}

export interface ReplaceRulesInput {
  staffId: string;
  rules: Array<{
    daysOfWeekMask: number;
    startMinuteOfDay: number;
    endMinuteOfDay: number;
    timezone?: string;
  }>;
}

export async function replaceRules(
  slug: string,
  input: ReplaceRulesInput,
): Promise<{ ok: true }> {
  return send(
    await fetch(
      `/api/admin/${encodeURIComponent(slug)}/availability/rules/replace`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    ),
  );
}

export interface CreateOverrideInput {
  staffId: string;
  rangeStart: number;
  rangeEnd: number;
  type: OverrideType;
  note?: string | null;
}

export async function createOverride(
  slug: string,
  input: CreateOverrideInput,
): Promise<{ ok: true; id: string; status: OverrideStatus }> {
  return send(
    await fetch(`/api/admin/${encodeURIComponent(slug)}/availability/override`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function deleteOverride(
  slug: string,
  overrideId: string,
): Promise<void> {
  const r = await fetch(
    `/api/admin/${encodeURIComponent(slug)}/availability/override/${encodeURIComponent(
      overrideId,
    )}`,
    { method: 'DELETE' },
  );
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new AvailabilityApiError(r.status, body.error ?? `http_${r.status}`);
  }
}

export async function decideOverride(
  slug: string,
  overrideId: string,
  decision: 'approve' | 'reject',
): Promise<{ ok: true; status: OverrideStatus }> {
  return send(
    await fetch(
      `/api/admin/${encodeURIComponent(slug)}/availability/override/${encodeURIComponent(
        overrideId,
      )}/decide`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ decision }),
      },
    ),
  );
}

export async function setConfirmedThrough(
  slug: string,
  userId: string,
  date: string | null,
): Promise<{ ok: true; availabilityConfirmedThrough: number | null }> {
  return send(
    await fetch(
      `/api/admin/${encodeURIComponent(slug)}/memberships/${encodeURIComponent(
        userId,
      )}/confirmed-through`,
      {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ date }),
      },
    ),
  );
}
